import React, { useState, useCallback, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import {
  Box,
  Switch,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Chip,
  Tooltip,
  InputAdornment,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LabelIcon from "@mui/icons-material/Label";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TagEditor from '../Widgets/TagEditor';

const SettingSendEmail = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();
  
  // Email Settings State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [smtpServer, setSmtpServer] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [useSSL, setUseSSL] = useState(true);
  const [useTLS, setUseTLS] = useState(true);
  const [authType, setAuthType] = useState('none');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [oauthSettings, setOauthSettings] = useState({
    clientId: '',
    clientSecret: '',
    refreshToken: '',
    accessToken: ''
  });
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [toEmails, setToEmails] = useState([{ email: '', name: '', type: 'to' }]);
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [isHtmlBody, setIsHtmlBody] = useState(true);
  const [priority, setPriority] = useState('normal');
  const [replyTo, setReplyTo] = useState('');
  const [customHeaders, setCustomHeaders] = useState([{ key: '', value: '' }]);
  const [attachments, setAttachments] = useState([]);
  const [readReceiptRequested, setReadReceiptRequested] = useState(false);
  const [deliveryNotification, setDeliveryNotification] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchSize, setBatchSize] = useState(10);
  const [delayBetweenEmails, setDelayBetweenEmails] = useState(1000);
  
  const smtpEditorRef = useRef(null);
  const fromEmailRef = useRef(null);
  const fromNameRef = useRef(null);
  const subjectRef = useRef(null);
  const messageBodyRef = useRef(null);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  // UI State
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    authentication: false,
    content: true,
    advanced: false
  });
  
  // Tags dropdown state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  // Handle menu open for tags
  const handleMenuOpen = (event, type) => {
    setMenuType(type);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'sendemail',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial) => {
      const filteredToEmails = toEmails.filter(email => email.email.trim());
      const filteredCustomHeaders = customHeaders.filter(header => header.key.trim() && header.value.trim());
      
      return name === (initial.name || '') &&
             description === (initial.description || '') &&
             smtpServer === (initial.smtpServer || '') &&
             parseInt(smtpPort) === (initial.smtpPort || 587) &&
             useSSL === (initial.useSSL !== undefined ? initial.useSSL : true) &&
             useTLS === (initial.useTLS !== undefined ? initial.useTLS : true) &&
             authType === (initial.authType || 'none') &&
             username === (initial.username || '') &&
             password === (initial.password || '') &&
             JSON.stringify(oauthSettings) === JSON.stringify(initial.oauthSettings || { clientId: '', clientSecret: '', refreshToken: '', accessToken: '' }) &&
             fromEmail === (initial.fromEmail || '') &&
             fromName === (initial.fromName || '') &&
             JSON.stringify(filteredToEmails) === JSON.stringify(initial.toEmails || [{ email: '', name: '', type: 'to' }]) &&
             subject === (initial.subject || '') &&
             messageBody === (initial.messageBody || '') &&
             isHtmlBody === (initial.isHtmlBody !== undefined ? initial.isHtmlBody : true) &&
             priority === (initial.priority || 'normal') &&
             replyTo === (initial.replyTo || '') &&
             JSON.stringify(filteredCustomHeaders) === JSON.stringify(initial.customHeaders || [{ key: '', value: '' }]) &&
             JSON.stringify(attachments) === JSON.stringify(initial.attachments || []) &&
             readReceiptRequested === (initial.readReceiptRequested || false) &&
             deliveryNotification === (initial.deliveryNotification || false) &&
             batchProcessing === (initial.batchProcessing || false) &&
             parseInt(batchSize) === (initial.batchSize || 10) &&
             parseInt(delayBetweenEmails) === (initial.delayBetweenEmails || 1000);
    },
    customSaveData: () => ({
      name,
      description,
      smtpServer,
      smtpPort: parseInt(smtpPort),
      useSSL,
      useTLS,
      authType,
      username,
      password,
      oauthSettings,
      fromEmail,
      fromName,
      toEmails: toEmails.filter(email => email.email.trim()),
      subject,
      messageBody,
      isHtmlBody,
      priority,
      replyTo,
      customHeaders: customHeaders.filter(header => header.key.trim() && header.value.trim()),
      attachments,
      readReceiptRequested,
      deliveryNotification,
      batchProcessing,
      batchSize: parseInt(batchSize),
      delayBetweenEmails: parseInt(delayBetweenEmails)
    }),
    loadDataFunction: (data) => {
      if (data.customData) {
        const emailData = data.customData;
        setName(emailData.name || '');
        setDescription(emailData.description || '');
        setSmtpServer(emailData.smtpServer || '');
        setSmtpPort(emailData.smtpPort || 587);
        setUseSSL(emailData.useSSL !== undefined ? emailData.useSSL : true);
        setUseTLS(emailData.useTLS !== undefined ? emailData.useTLS : true);
        setAuthType(emailData.authType || 'none');
        setUsername(emailData.username || '');
        setPassword(emailData.password || '');
        setOauthSettings(emailData.oauthSettings || {
          clientId: '',
          clientSecret: '',
          refreshToken: '',
          accessToken: ''
        });
        setFromEmail(emailData.fromEmail || '');
        setFromName(emailData.fromName || '');
        setToEmails(emailData.toEmails || [{ email: '', name: '', type: 'to' }]);
        setSubject(emailData.subject || '');
        setMessageBody(emailData.messageBody || '');
        setIsHtmlBody(emailData.isHtmlBody !== undefined ? emailData.isHtmlBody : true);
        setPriority(emailData.priority || 'normal');
        setReplyTo(emailData.replyTo || '');
        setCustomHeaders(emailData.customHeaders || [{ key: '', value: '' }]);
        setAttachments(emailData.attachments || []);
        setReadReceiptRequested(emailData.readReceiptRequested || false);
        setDeliveryNotification(emailData.deliveryNotification || false);
        setBatchProcessing(emailData.batchProcessing || false);
        setBatchSize(emailData.batchSize || 10);
        setDelayBetweenEmails(emailData.delayBetweenEmails || 1000);
      }
    }
  });

  // UI handler functions
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const addEmailRecipient = () => {
    setToEmails([...toEmails, { email: '', name: '', type: 'to' }]);
  };

  const removeEmailRecipient = (index) => {
    setToEmails(toEmails.filter((_, i) => i !== index));
  };

  const updateEmailRecipient = (index, field, value) => {
    const updated = [...toEmails];
    updated[index][field] = value;
    setToEmails(updated);
  };

  const addCustomHeader = () => {
    setCustomHeaders([...customHeaders, { key: '', value: '' }]);
  };

  const removeCustomHeader = (index) => {
    setCustomHeaders(customHeaders.filter((_, i) => i !== index));
  };

  const updateCustomHeader = (index, field, value) => {
    const updated = [...customHeaders];
    updated[index][field] = value;
    setCustomHeaders(updated);
  };

  const handleTagSelect = useCallback((value) => {
    if (menuType === 'subject' && subjectRef.current) {
      subjectRef.current.insertTag(value);
    } else if (menuType === 'fromEmail' && fromEmailRef.current) {
      fromEmailRef.current.insertTag(value);
    } else if (menuType === 'fromName' && fromNameRef.current) {
      fromNameRef.current.insertTag(value);
    } else if (menuType === 'smtpServer' && smtpEditorRef.current) {
      smtpEditorRef.current.insertTag(value);
    } else if (menuType === 'messageBody' && messageBodyRef.current) {
      messageBodyRef.current.insertTag(value);
    } else if (menuType === 'username' && usernameRef.current) {
      usernameRef.current.insertTag(value);
    } else if (menuType === 'password' && passwordRef.current) {
      passwordRef.current.insertTag(value);
    } else if (menuType.startsWith('recipient_')) {
      const index = parseInt(menuType.split('_')[1]);
      const field = menuType.split('_')[2];
      const updated = [...toEmails];
      updated[index][field] = (updated[index][field] || '') + `{${value}}`;
      setToEmails(updated);
    } else if (menuType.startsWith('header_')) {
      const index = parseInt(menuType.split('_')[1]);
      const field = menuType.split('_')[2];
      const updated = [...customHeaders];
      updated[index][field] = (updated[index][field] || '') + `{${value}}`;
      setCustomHeaders(updated);
    }
    
    setAnchorEl(null);
  }, [menuType, toEmails, customHeaders]);

  return (
    <>
      <ActionSettingsLayout
      title={t('emailSetting.title.edit', 'Email Settings')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      onSave={handleSave} // Save handler
      onForceRun={handleForceRun}
      isSaveDisabled={isSaveDisabled}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      tagsDropdownProps={{
        tags: tags,
        handleTagSelect: handleTagSelect,
        anchorEl: anchorEl,
        setAnchorEl: handleMenuClose,
      }}
    >
      {/* Email Configuration Content */}
      <Grid container item xs={12} spacing={0}>
        {/* Basic Settings */}
        <Accordion 
          expanded={expandedSections.basic} 
          onChange={() => toggleSection('basic')}
          sx={{ mb: 2, width: '100%' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} variant="h6">{t('emailSetting.sections.basic', 'Basic Settings')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('emailSetting.fields.name', 'Name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('emailSetting.fields.description', 'Description')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                  {t('emailSetting.fields.smtpServer', 'SMTP Server')} *
                </Typography>
                <Box sx={{
                  border: '1px solid #d0d0e8',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: '#5757b0',
                  },
                }}>
                  <Box display="flex" alignItems="center">
                    <Box sx={{ flexGrow: 1 }}>
                      <TagEditor
                        ref={smtpEditorRef}
                        value={smtpServer}
                        onChange={setSmtpServer}
                        placeholder="e.g. smtp.gmail.com"
                        singleLine
                        onTagClick={(e) => handleMenuOpen(e, 'smtpServer')}
                        tags={tags}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('emailSetting.fields.smtpPort', 'SMTP Port')}
                  type="number"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(parseInt(e.target.value) || 587)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useSSL}
                      onChange={(e) => setUseSSL(e.target.checked)}
                    />
                  }
                  label={t('emailSetting.fields.useSSL', 'Use SSL')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={useTLS}
                      onChange={(e) => setUseTLS(e.target.checked)}
                    />
                  }
                  label={t('emailSetting.fields.useTLS', 'Use TLS')}
                  sx={{ ml: 2 }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Authentication */}
        <Accordion 
          expanded={expandedSections.authentication} 
          onChange={() => toggleSection('authentication')}
          sx={{ mb: 2, width: '100%' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} variant="h6">{t('emailSetting.sections.authentication', 'Authentication')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('emailSetting.fields.authType', 'Authentication Type')}</InputLabel>
                  <Select
                    value={authType}
                    onChange={(e) => setAuthType(e.target.value)}
                    label={t('emailSetting.fields.authType', 'Authentication Type')}
                  >
                    <MenuItem value="none">{t('emailSetting.auth.none', 'None')}</MenuItem>
                    <MenuItem value="basic">{t('emailSetting.auth.basic', 'Basic (Username/Password)')}</MenuItem>
                    <MenuItem value="oauth2">{t('emailSetting.auth.oauth2', 'OAuth 2.0')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {authType === 'basic' && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                      {t('emailSetting.fields.username', 'Username')}
                    </Typography>
                    <Box sx={{
                      border: '1px solid #d0d0e8',
                      borderRadius: '12px',
                      backgroundColor: '#fff',
                      overflow: 'hidden',
                      '&:hover': { borderColor: '#5757b0' },
                    }}>
                      <TagEditor
                        ref={usernameRef}
                        value={username}
                        onChange={setUsername}
                        placeholder={t('emailSetting.fields.username', 'Username')}
                        singleLine
                        onTagClick={(e) => handleMenuOpen(e, 'username')}
                        tags={tags}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                      {t('emailSetting.fields.password', 'Password')}
                    </Typography>
                    <Box sx={{
                      border: '1px solid #d0d0e8',
                      borderRadius: '12px',
                      backgroundColor: '#fff',
                      overflow: 'hidden',
                      '&:hover': { borderColor: '#5757b0' },
                    }}>
                      <TagEditor
                        ref={passwordRef}
                        value={password}
                        onChange={setPassword}
                        placeholder={t('emailSetting.fields.password', 'Password')}
                        singleLine
                        onTagClick={(e) => handleMenuOpen(e, 'password')}
                        tags={tags}
                      />
                    </Box>
                  </Grid>
                </>
              )}
              
              {authType === 'oauth2' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main' }}>
                      {t('emailSetting.oauth.setup', 'OAuth 2.0 Setup')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {t('emailSetting.oauth.instructions', 'Configure OAuth 2.0 credentials for your email provider. Each provider has different requirements.')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('emailSetting.fields.clientId', 'Client ID')}
                      value={oauthSettings.clientId}
                      onChange={(e) => setOauthSettings(prev => ({ ...prev, clientId: e.target.value }))}
                      placeholder={t('emailSetting.placeholder.clientId', 'Your OAuth Client ID')}
                      helperText={t('emailSetting.help.clientId', 'OAuth Client ID from your email provider')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="password"
                      label={t('emailSetting.fields.clientSecret', 'Client Secret')}
                      value={oauthSettings.clientSecret}
                      onChange={(e) => setOauthSettings(prev => ({ ...prev, clientSecret: e.target.value }))}
                      placeholder={t('emailSetting.placeholder.clientSecret', 'Your OAuth Client Secret')}
                      helperText={t('emailSetting.help.clientSecret', 'OAuth Client Secret from your email provider')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('emailSetting.fields.refreshToken', 'Refresh Token')}
                      value={oauthSettings.refreshToken}
                      onChange={(e) => setOauthSettings(prev => ({ ...prev, refreshToken: e.target.value }))}
                      placeholder={t('emailSetting.placeholder.refreshToken', 'Your OAuth Refresh Token')}
                      helperText={t('emailSetting.help.refreshToken', 'OAuth Refresh Token obtained during authorization flow')}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: 'info.main', mb: 1, display: 'block' }}>
                      {t('emailSetting.oauth.providerExamples', 'Common OAuth 2.0 Email Providers:')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      • <strong>Gmail:</strong> smtp.gmail.com:587 (Google Cloud Console)<br/>
                      • <strong>Outlook:</strong> smtp.live.com:587 (Azure App Registration)<br/>
                      • <strong>Yahoo:</strong> smtp.mail.yahoo.com:587 (Yahoo Developer)<br/>
                      • <strong>Custom:</strong> Refer to your provider's OAuth documentation
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Email Content */}
        <Accordion 
          expanded={expandedSections.content} 
          onChange={() => toggleSection('content')}
          sx={{ mb: 2, width: '100%' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} variant="h6">{t('emailSetting.sections.content', 'Email Content')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                  {t('emailSetting.fields.fromEmail', 'From Email')} *
                </Typography>
                <Box sx={{
                  border: '1px solid #d0d0e8',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                  '&:hover': { borderColor: '#5757b0' },
                }}>
                  <Box display="flex" alignItems="center">
                    <Box sx={{ flexGrow: 1 }}>
                      <TagEditor
                        ref={fromEmailRef}
                        value={fromEmail}
                        onChange={setFromEmail}
                        placeholder="sender@example.com"
                        singleLine
                        onTagClick={(e) => handleMenuOpen(e, 'fromEmail')}
                        tags={tags}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                  {t('emailSetting.fields.fromName', 'From Name')}
                </Typography>
                <Box sx={{
                  border: '1px solid #d0d0e8',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                  '&:hover': { borderColor: '#5757b0' },
                }}>
                  <Box display="flex" alignItems="center">
                    <Box sx={{ flexGrow: 1 }}>
                      <TagEditor
                        ref={fromNameRef}
                        value={fromName}
                        onChange={setFromName}
                        placeholder="Sender Name"
                        singleLine
                        tags={tags}
                        onTagClick={(e) => handleMenuOpen(e, 'fromName')}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Recipients */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    {t('emailSetting.fields.recipients', 'Recipients')}
                  </Typography>
                  <IconButton onClick={addEmailRecipient} size="small">
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Box>
                
                {toEmails.map((recipient, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 1 }}>
                    <FormControl sx={{ minWidth: 80, mr: 1 }}>
                      <Select
                        value={recipient.type}
                        onChange={(e) => updateEmailRecipient(index, 'type', e.target.value)}
                        size="small"
                      >
                        <MenuItem value="to">To</MenuItem>
                        <MenuItem value="cc">CC</MenuItem>
                        <MenuItem value="bcc">BCC</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Box sx={{ flexGrow: 1, mr: 1, borderRadius: '8px', overflow: 'hidden' }}>
                      <TagEditor
                        value={recipient.email}
                        onChange={(val) => updateEmailRecipient(index, 'email', val)}
                        placeholder="Email"
                        minHeight="35px"
                        singleLine
                        tags={tags}
                      />
                    </Box>

                    <Box sx={{ minWidth: 150, mr: 1, borderRadius: '8px', overflow: 'hidden' }}>
                      <TagEditor
                         value={recipient.name}
                         onChange={(val) => updateEmailRecipient(index, 'name', val)}
                         placeholder="Name"
                         minHeight="35px"
                         singleLine
                         onTagClick={(e) => handleMenuOpen(e, `recipient_${index}_name`)}
                         tags={tags}
                      />
                    </Box>

                    <IconButton 
                      onClick={() => removeEmailRecipient(index)} 
                      disabled={toEmails.length === 1}
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                  {t('emailSetting.fields.subject', 'Subject')} *
                </Typography>
                <Box sx={{
                  border: '1px solid #d0d0e8',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                  '&:hover': { borderColor: '#5757b0' },
                }}>
                  <Box display="flex" alignItems="center">
                    <Box sx={{ flexGrow: 1 }}>
                      <TagEditor
                        ref={subjectRef}
                        value={subject}
                        onChange={setSubject}
                        placeholder="Email Subject"
                        tags={tags}
                        singleLine
                        onTagClick={(e) => handleMenuOpen(e, 'subject')}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ mr: 2 }}>
                    {t('emailSetting.fields.messageBody', 'Message Body')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isHtmlBody}
                        onChange={(e) => setIsHtmlBody(e.target.checked)}
                        size="small"
                      />
                    }
                    label={t('emailSetting.fields.htmlBody', 'HTML Format')}
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Box sx={{
                  border: '1px solid #d0d0e8',
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  minHeight: 200,
                  overflow: 'hidden',
                  '&:hover': { borderColor: '#5757b0' },
                }}>
                  <TagEditor
                    ref={messageBodyRef}
                    value={messageBody}
                    tags={tags}
                    onChange={setMessageBody}
                    placeholder={t('emailSetting.placeholders.messageBody', 'Enter your email message...')}
                    onTagClick={(e) => handleMenuOpen(e, 'messageBody')}
                  />
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Advanced Settings */}
        <Accordion 
          expanded={expandedSections.advanced} 
          onChange={() => toggleSection('advanced')}
          sx={{ mb: 2, width: '100%' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} variant="h6">{t('emailSetting.sections.advanced', 'Advanced Settings')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('emailSetting.fields.priority', 'Priority')}</InputLabel>
                  <Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    label={t('emailSetting.fields.priority', 'Priority')}
                  >
                    <MenuItem value="low">{t('emailSetting.priority.low', 'Low')}</MenuItem>
                    <MenuItem value="normal">{t('emailSetting.priority.normal', 'Normal')}</MenuItem>
                    <MenuItem value="high">{t('emailSetting.priority.high', 'High')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('emailSetting.fields.replyTo', 'Reply To')}
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                />
              </Grid>

              {/* Custom Headers */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    {t('emailSetting.fields.customHeaders', 'Custom Headers')}
                  </Typography>
                  <IconButton onClick={addCustomHeader} size="small">
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Box>
                
                {customHeaders.map((header, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ flexBasis: '40%', mr: 1, borderRadius: '8px', overflow: 'hidden' }}>
                      <TagEditor
                         value={header.key}
                         onChange={(val) => updateCustomHeader(index, 'key', val)}
                         placeholder="Header Key"
                         minHeight="35px"
                         tags={tags}
                      />
                    </Box>

                    <Box sx={{ flexGrow: 1, mr: 1, borderRadius: '8px', overflow: 'hidden' }}>
                      <TagEditor
                         value={header.value}
                         onChange={(val) => updateCustomHeader(index, 'value', val)}
                         placeholder="Header Value"
                         minHeight="35px"
                         singleLine
                         onTagClick={(e) => handleMenuOpen(e, `header_${index}_value`)}
                         tags={tags}
                      />
                    </Box>

                    <IconButton 
                      onClick={() => removeCustomHeader(index)} 
                      disabled={customHeaders.length === 1 && index === 0}
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>

              {/* Batch Processing */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={batchProcessing}
                      onChange={(e) => setBatchProcessing(e.target.checked)}
                    />
                  }
                  label={t('emailSetting.fields.batchProcessing', 'Batch Processing')}
                />
                
                {batchProcessing && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('emailSetting.fields.batchSize', 'Batch Size')}
                          type="number"
                          value={batchSize}
                          onChange={(e) => setBatchSize(parseInt(e.target.value) || 10)}
                          inputProps={{ min: 1, max: 100 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('emailSetting.fields.delayBetweenEmails', 'Delay Between Emails (ms)')}
                          type="number"
                          value={delayBetweenEmails}
                          onChange={(e) => setDelayBetweenEmails(parseInt(e.target.value) || 1000)}
                          inputProps={{ min: 0, max: 60000 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>

              {/* Request Options */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={readReceiptRequested}
                      onChange={(e) => setReadReceiptRequested(e.target.checked)}
                    />
                  }
                  label={t('emailSetting.fields.readReceipt', 'Request Read Receipt')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={deliveryNotification}
                      onChange={(e) => setDeliveryNotification(e.target.checked)}
                    />
                  }
                  label={t('emailSetting.fields.deliveryNotification', 'Request Delivery Notification')}
                  sx={{ ml: 2 }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
      </ActionSettingsLayout>
    </>
  );
};

export default SettingSendEmail;
